import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Search, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import * as XLSX from "xlsx";
import { RevoGrid } from "@revolist/react-datagrid";
import { usePeriodeStore } from "@/hooks/use-periode";
import { getAnalytics70, type Analytics70Item } from "./api/analytics-70";
import remunData from "./api/remun-data.json";

export default function Analytics70TablePage() {
  const { getYearAndSemester } = usePeriodeStore();
  const { year, semester, periodeId } = getYearAndSemester();

  const targetYear = semester === 2 ? year + 1 : year;
  const defaultMonth = semester === 2 ? "06" : "07";
  const [selectedMonth, setSelectedMonth] = useState(
    `${targetYear}_${defaultMonth}`,
  );
  const [searchQuery, setSearchQuery] = useState("");

  const { data: analyticsData = [], isLoading } = useQuery({
    queryKey: ["analytics-70", year, semester, periodeId, selectedMonth],
    queryFn: () => getAnalytics70(year, semester, periodeId!, selectedMonth),
    enabled: !!year && !!semester && !!periodeId && !!selectedMonth,
  });

  const excelDataMap = useMemo(() => {
    const map: Record<string, any> = {};
    (remunData as any[]).forEach((row) => {
      if (row.NIP) {
        let nipStr = String(row.NIP).trim();
        if (nipStr.startsWith("'")) {
          nipStr = nipStr.substring(1);
        }
        map[nipStr] = row;
      }
    });
    return map;
  }, []);

  const loading = isLoading;

  // Extract all keys from Analytics70Item except "Bulan"
  const columns: (keyof Analytics70Item)[] = [
    "NIP",
    "StatusSekarang",
    "StatusPegawai",
    "NamaDosen",
    "Jurusan",
    "Fakultas",
    "Golongan",
    "Fungsional",
    "Struktural",
    "Cluster",
    "ISPenelitian",
    "ISPengabdian",
    "SKSSebelumDikurang",
    "PointSKSFinal",
    "KonstantaFungsional",
    "PendidikanLainnya_SKS",
    "PendidikanLainnya_Point",
    "PendidikanLainnya_Batas",
    "SisaPendidikanPoint",
    "TotalPenunjangSebelumDikurang",
    "Penunjang_SKS",
    "Penunjang_Point",
    "Penunjang_Batas",
    "SisaPenunjangPoint",
    "TotalPoinKinerjaDasar",
    "TotalPointIKU",
    "PointPerformance",
    "PointPosition",
    "PointPositionCluster",
    "TotalPoint",
    "PIR",
    "Rupiah100Performance",
    "Rupiah100Position",
    "Rupiah100Total",
    "Rupiah100Kotor",
    "Realisasi30",
    "Rupiah70Kotor",
    "RupiahBersih70",
  ];

  const excelColumns = [
    "JUMLAH KOTOR 100% (Rp)",
    "PPh 100%",
    "JUMLAH KOTOR 30% (Rp)",
    "JUMLAH KOTOR 70% (Rp)",
    "PPh 21",
    "JUMLAH BERSIH 70% (Rp)",
  ];

  const pinnedApiCols = [
    "Rupiah100Kotor",
    "Realisasi30",
    "Rupiah70Kotor",
    "RupiahBersih70",
  ];
  const pinnedExcelCols = [
    "JUMLAH KOTOR 100% (Rp)",
    "JUMLAH KOTOR 30% (Rp)",
    "JUMLAH KOTOR 70% (Rp)",
    "JUMLAH BERSIH 70% (Rp)",
  ];

  const normalColumns = columns.filter(
    (c) => !pinnedApiCols.includes(c as string),
  );
  const normalExcelColumns = excelColumns.filter(
    (c) => !pinnedExcelCols.includes(c),
  );

  const formatRupiah = (val: any) => {
    if (val === undefined || val === null || val === "" || val === "-") {
      val = 0;
    }
    const num = Number(val);
    if (isNaN(num)) return val;
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const isRupiahColumn = (col: string) => {
    if (!col) return false;
    const lower = col.toLowerCase();
    return (
      lower.includes("rupiah") ||
      lower.includes("realisasi30") ||
      lower.includes("(rp)") ||
      lower.includes("pph") ||
      lower.includes("selisih")
    );
  };

  const parseVal = (v: any) => {
    if (v === undefined || v === null || v === "" || v === "-") return 0;
    if (typeof v === "string") {
      const cleaned = v.replace(/[^0-9.-]+/g, "");
      const num = Number(cleaned);
      return isNaN(num) ? 0 : num;
    }
    const num = Number(v);
    return isNaN(num) ? 0 : num;
  };

  const numericCompare = (prop: string, a: any, b: any) => {
    const valA = parseVal(a[prop]);
    const valB = parseVal(b[prop]);
    return valA - valB;
  };

  const createCellTemplate = (bgColor: string, textColor: string) => {
    return (h: any, props: any) => {
      const isRupiah = isRupiahColumn(props.prop);
      const val = isRupiah
        ? formatRupiah(props.model[props.prop])
        : props.model[props.prop];
      return h(
        "div",
        {
          style: {
            backgroundColor: bgColor,
            color: textColor,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            padding: "0 4px",
            fontWeight: "bold",
            userSelect: "text",
            fontSize: "10px",
          },
        },
        val,
      );
    };
  };

  const normalCellTemplate = (h: any, props: any) => {
    const isRupiah = isRupiahColumn(props.prop);
    const val = isRupiah
      ? formatRupiah(props.model[props.prop])
      : props.model[props.prop];
    return h(
      "div",
      {
        style: {
          userSelect: "text",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          padding: "0 4px",
          fontSize: "10px",
        },
      },
      val,
    );
  };

  const wrapCellTemplate = (h: any, props: any) => {
    const isRupiah = isRupiahColumn(props.prop);
    const val = isRupiah
      ? formatRupiah(props.model[props.prop])
      : props.model[props.prop];
    return h(
      "div",
      {
        style: {
          userSelect: "text",
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
          padding: "2px 4px",
          width: "100%",
          height: "100%",
          fontSize: "10px",
        },
      },
      val,
    );
  };

  const revoColumns = [
    {
      prop: "index",
      name: "No",
      size: 40,
      pin: "colPinStart" as const,
      cellTemplate: normalCellTemplate,
      sortable: true,
    },
    ...normalColumns.map((col) => {
      const isNama = col === "NamaDosen";
      return {
        prop: col,
        name: col,
        size: isNama ? 200 : 100,
        cellTemplate: isNama ? wrapCellTemplate : normalCellTemplate,
        sortable: true,
        ...(isRupiahColumn(col as string)
          ? { cellCompare: numericCompare }
          : {}),
      };
    }),
    ...normalExcelColumns.map((col) => {
      const isNama = col === "NAMA DOSEN";
      return {
        prop: `Excel_${col}`,
        name: `Lama: ${col}`,
        size: isNama ? 200 : 120,
        cellTemplate: isNama ? wrapCellTemplate : normalCellTemplate,
        sortable: true,
        ...(isRupiahColumn(col) ? { cellCompare: numericCompare } : {}),
      };
    }),
    {
      prop: "RupiahSerdos",
      name: "Baru: Rupiah Serdos",
      size: 120,
      cellTemplate: createCellTemplate("#dcfce3", "#14532d"),
      sortable: true,
      cellCompare: numericCompare,
    },
    {
      prop: "Rupiah100Kotor",
      name: "Baru: 100% Kotor",
      size: 120,
      cellTemplate: createCellTemplate("#dbeafe", "#1e3a8a"),
      sortable: true,
      cellCompare: numericCompare,
    },
    {
      prop: "Excel_JUMLAH KOTOR 100% (Rp)",
      name: "Lama: 100% Kotor",
      size: 130,
      cellTemplate: createCellTemplate("#dbeafe", "#1e3a8a"),
      sortable: true,
      cellCompare: numericCompare,
    },
    {
      prop: "Selisih100Kotor",
      name: "Selisih 100% Kotor",
      size: 120,
      cellTemplate: createCellTemplate("#fef08a", "#854d0e"),
      sortable: true,
      cellCompare: numericCompare,
    },
    {
      prop: "Realisasi30",
      name: "Baru: 30% Kotor",
      size: 120,
      pin: "colPinEnd" as const,
      cellTemplate: createCellTemplate("#fef3c7", "#78350f"),
      sortable: true,
      cellCompare: numericCompare,
    },
    {
      prop: "Excel_JUMLAH KOTOR 30% (Rp)",
      name: "Lama: 30% Kotor",
      size: 130,
      pin: "colPinEnd" as const,
      cellTemplate: createCellTemplate("#fef3c7", "#78350f"),
      sortable: true,
      cellCompare: numericCompare,
    },
    {
      prop: "Rupiah70Kotor",
      name: "Baru: 70% Kotor",
      size: 120,
      pin: "colPinEnd" as const,
      cellTemplate: createCellTemplate("#f3e8ff", "#581c87"),
      sortable: true,
      cellCompare: numericCompare,
    },
    {
      prop: "Excel_JUMLAH KOTOR 70% (Rp)",
      name: "Lama: 70% Kotor",
      size: 130,
      pin: "colPinEnd" as const,
      cellTemplate: createCellTemplate("#f3e8ff", "#581c87"),
      sortable: true,
      cellCompare: numericCompare,
    },
    {
      prop: "RupiahBersih70",
      name: "Baru: Bersih 70%",
      size: 120,
      pin: "colPinEnd" as const,
      cellTemplate: createCellTemplate("#d1fae5", "#064e3b"),
      sortable: true,
      cellCompare: numericCompare,
    },
    {
      prop: "Excel_JUMLAH BERSIH 70% (Rp)",
      name: "Lama: Bersih 70%",
      size: 130,
      pin: "colPinEnd" as const,
      cellTemplate: createCellTemplate("#d1fae5", "#064e3b"),
      sortable: true,
      cellCompare: numericCompare,
    },
    {
      prop: "Baru3070Kotor",
      name: "Baru: 30+70 Kotor",
      size: 130,
      pin: "colPinEnd" as const,
      cellTemplate: createCellTemplate("#e0e7ff", "#3730a3"),
      sortable: true,
      cellCompare: numericCompare,
    },
    {
      prop: "Lama3070Kotor",
      name: "Lama: 30+70 Kotor",
      size: 130,
      pin: "colPinEnd" as const,
      cellTemplate: createCellTemplate("#e0e7ff", "#3730a3"),
      sortable: true,
      cellCompare: numericCompare,
    },
    {
      prop: "Selisih3070Kotor",
      name: "Selisih 30+70",
      size: 120,
      pin: "colPinEnd" as const,
      cellTemplate: createCellTemplate("#fef08a", "#854d0e"),
      sortable: true,
      cellCompare: numericCompare,
    },
  ] as any;

  const filteredData = useMemo(() => {
    let result = analyticsData;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((item) => {
        const nip = String(item.NIP || "").toLowerCase();
        const nama = String(item.NamaDosen || "").toLowerCase();
        return nip.includes(query) || nama.includes(query);
      });
    }

    return result;
  }, [analyticsData, searchQuery]);

  const handleExportExcel = () => {
    const exportData = filteredData.map((item, index) => {
      const nip = String(item.NIP).trim();
      const excelRow = excelDataMap[nip] || {};

      const row: any = {
        No: index + 1,
      };

      // Add normal columns
      normalColumns.forEach((col) => {
        let val = item[col as keyof typeof item];
        if (isRupiahColumn(col as string)) {
          val = parseVal(val) as any;
        }
        row[col as string] = val;
      });

      // Add normal excel columns
      normalExcelColumns.forEach((col) => {
        let val = excelRow[col];
        if (isRupiahColumn(col)) {
          val = parseVal(val) as any;
        } else if (val === undefined || val === null) {
          val = "-";
        }
        row[`Lama: ${col}`] = val;
      });

      // Pinned API cols and specific comparisons
      row["Baru: 100% Kotor"] = parseVal(item.Rupiah100Kotor);
      row["Baru: Rupiah Serdos"] = parseVal((item as any).RupiahSerdos);
      row["Lama: 100% Kotor"] = parseVal(excelRow["JUMLAH KOTOR 100% (Rp)"]);
      row["Selisih 100% Kotor"] =
        parseVal(item.Rupiah100Kotor) -
        parseVal(excelRow["JUMLAH KOTOR 100% (Rp)"]);
      row["Baru: 30% Kotor"] = parseVal(item.Realisasi30);
      row["Lama: 30% Kotor"] = parseVal(excelRow["JUMLAH KOTOR 30% (Rp)"]);
      row["Baru: 70% Kotor"] = parseVal(item.Rupiah70Kotor);
      row["Lama: 70% Kotor"] = parseVal(excelRow["JUMLAH KOTOR 70% (Rp)"]);

      const valBaru30 = parseVal(item.Realisasi30);
      const valBaru70 = parseVal(item.Rupiah70Kotor);
      const valLama30 = parseVal(excelRow["JUMLAH KOTOR 30% (Rp)"]);
      const valLama70 = parseVal(excelRow["JUMLAH KOTOR 70% (Rp)"]);

      row["Baru: Bersih 70%"] = parseVal(item.RupiahBersih70);
      row["Lama: Bersih 70%"] = parseVal(excelRow["JUMLAH BERSIH 70% (Rp)"]);

      row["Baru: 30+70 Kotor"] = valBaru30 + valBaru70;
      row["Lama: 30+70 Kotor"] = valLama30 + valLama70;
      row["Selisih 30+70"] = (valBaru30 + valBaru70) - (valLama30 + valLama70);

      return row;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data Remunerasi 70%");
    XLSX.writeFile(
      wb,
      `Data_Remunerasi_70_${year}_${semester}_${selectedMonth}.xlsx`,
    );
  };

  const revoSource = filteredData.map((item, index) => {
    const nip = String(item.NIP).trim();
    const excelRow = excelDataMap[nip] || {};

    const formattedItem: any = { index: index + 1, ...item };

    // Format Excel columns
    excelColumns.forEach((col) => {
      const rawVal = excelRow[col] ?? "-";
      formattedItem[`Excel_${col}`] = rawVal;
    });

    formattedItem.Selisih100Kotor =
      parseVal(item.Rupiah100Kotor) -
      parseVal(excelRow["JUMLAH KOTOR 100% (Rp)"]);

    const valBaru30 = parseVal(item.Realisasi30);
    const valBaru70 = parseVal(item.Rupiah70Kotor);
    const valLama30 = parseVal(excelRow["JUMLAH KOTOR 30% (Rp)"]);
    const valLama70 = parseVal(excelRow["JUMLAH KOTOR 70% (Rp)"]);

    formattedItem.Baru3070Kotor = valBaru30 + valBaru70;
    formattedItem.Lama3070Kotor = valLama30 + valLama70;
    formattedItem.Selisih3070Kotor = formattedItem.Baru3070Kotor - formattedItem.Lama3070Kotor;

    return formattedItem;
  });

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 w-full max-w-[100vw] overflow-hidden">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">
            Data Analitik Remunerasi 70%
          </h1>
          <p className="text-slate-500 font-medium">
            Tabel rekapitulasi data dosen untuk remunerasi 70%
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-end sm:items-center gap-3">
          <div className="relative flex items-center">
            <Search className="w-4 h-4 absolute left-3 text-slate-400" />
            <Input
              type="text"
              placeholder="Cari Nama / NIP..."
              className="pl-9 w-64 bg-white"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-600 ml-2">
              Bulan:
            </span>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-45 bg-white font-bold">
                <SelectValue placeholder="Pilih Bulan" />
              </SelectTrigger>
              <SelectContent>
                {(semester === 2
                  ? [1, 2, 3, 4, 5, 6]
                  : [7, 8, 9, 10, 11, 12]
                ).map((m) => {
                  const mYear = semester === 2 ? year + 1 : year;
                  const val = `${mYear}_${m.toString().padStart(2, "0")}`;
                  const monthNames = [
                    "Januari",
                    "Februari",
                    "Maret",
                    "April",
                    "Mei",
                    "Juni",
                    "Juli",
                    "Agustus",
                    "September",
                    "Oktober",
                    "November",
                    "Desember",
                  ];
                  return (
                    <SelectItem key={val} value={val}>
                      {monthNames[m - 1]} {mYear}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Button
              onClick={handleExportExcel}
              disabled={loading || filteredData.length === 0}
              variant="outline"
              className="bg-white hover:bg-slate-50 text-slate-700 font-bold ml-2"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
          </div>
        </div>
      </div>

      <Card className="rounded-xl shadow-sm border-muted/40 overflow-hidden">
        <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-4">
          <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
            Data Tabel Analitik
            {loading && (
              <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
            )}
          </CardTitle>
          <CardDescription>
            Menampilkan {filteredData.length} data dosen.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="w-full h-150 relative">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <Loader2 className="w-8 h-8 animate-spin mb-4 text-indigo-500" />
                <span>Memuat data...</span>
              </div>
            ) : analyticsData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <span>Tidak ada data ditemukan untuk bulan ini.</span>
              </div>
            ) : (
              <>
                <style>{`
                  revogr-header .rgHeaderCell {
                    font-size: 10px !important;
                    padding: 0 4px !important;
                    line-height: 1.2 !important;
                  }
                `}</style>
                <RevoGrid
                  theme="material"
                  columns={revoColumns}
                  source={revoSource}
                  readonly={true}
                  rowSize={30}
                />
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
